import { useCallback, useEffect, useState } from "react";
import { Check, Mail, Phone, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApiClient } from "../api/client";
import { useI18n } from "../i18n/I18nProvider";
import { PageHeader } from "../components/PageHeader";
import { ConfirmModal } from "../components/ConfirmModal";

interface Lead {
  id: string;
  created_at: string;
  name: string;
  contact: string;
  contact_type: "email" | "phone";
  need: string;
  reason: "no_answer" | "human_request" | "quote" | "other";
  status: "new" | "handled";
}

type Filter = "" | "new" | "handled";

export function LeadsPage() {
  const api = useApiClient();
  const { t } = useI18n();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [filter, setFilter] = useState<Filter>("");
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState<Lead | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter ? `?status=${filter}` : "";
      const res = await api<{ leads: Lead[] }>(`/tenant/api/leads${qs}`);
      setLeads(res.leads);
    } catch {
      toast.error("Error");
    } finally {
      setLoading(false);
    }
  }, [api, filter]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function setStatus(lead: Lead, status: "new" | "handled") {
    try {
      await api(`/tenant/api/leads/${lead.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      // Si un filtre est actif et que le nouveau statut ne correspond plus,
      // on retire la ligne de la vue (cohérence avec le filtre) ; sinon on
      // met juste le statut à jour en place.
      setLeads((prev) =>
        filter && filter !== status
          ? prev.filter((l) => l.id !== lead.id)
          : prev.map((l) => (l.id === lead.id ? { ...l, status } : l)),
      );
    } catch {
      toast.error("Error");
    }
  }

  async function doDelete() {
    if (!toDelete) return;
    const id = toDelete.id;
    setToDelete(null);
    try {
      await api(`/tenant/api/leads/${id}`, { method: "DELETE" });
      setLeads((prev) => prev.filter((l) => l.id !== id));
    } catch {
      toast.error("Error");
    }
  }

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const FILTERS: { key: Filter; label: string }[] = [
    { key: "", label: t("leads_filter_all") },
    { key: "new", label: t("leads_filter_new") },
    { key: "handled", label: t("leads_filter_handled") },
  ];

  return (
    <div className="page is-active">
      <PageHeader titleKey="leads_title" subtitleKey="leads_subtitle" />

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            type="button"
            className={"btn-sm " + (filter === f.key ? "btn-primary" : "btn-ghost")}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? null : leads.length === 0 ? (
        <p style={{ color: "var(--text-muted)" }}>{t("leads_empty")}</p>
      ) : (
        <div className="card" style={{ overflowX: "auto", padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--text-muted)", fontSize: 12 }}>
                <th style={{ padding: "12px 16px" }}>{t("leads_col_date")}</th>
                <th style={{ padding: "12px 16px" }}>{t("leads_col_name")}</th>
                <th style={{ padding: "12px 16px" }}>{t("leads_col_contact")}</th>
                <th style={{ padding: "12px 16px" }}>{t("leads_col_need")}</th>
                <th style={{ padding: "12px 16px" }}>{t("leads_col_reason")}</th>
                <th style={{ padding: "12px 16px" }}>{t("leads_col_status")}</th>
                <th style={{ padding: "12px 16px" }} />
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "12px 16px", whiteSpace: "nowrap", color: "var(--text-muted)" }}>
                    {fmtDate(l.created_at)}
                  </td>
                  <td style={{ padding: "12px 16px", fontWeight: 600 }}>{l.name}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <a
                      href={
                        l.contact_type === "email"
                          ? `mailto:${l.contact}`
                          : `tel:${l.contact}`
                      }
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--purple)", textDecoration: "none" }}
                    >
                      {l.contact_type === "email" ? <Mail size={13} /> : <Phone size={13} />}
                      {l.contact}
                    </a>
                  </td>
                  <td style={{ padding: "12px 16px", maxWidth: 320 }}>{l.need}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span className="badge">{t(`leads_reason_${l.reason}`)}</span>
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    <span className={"badge " + (l.status === "new" ? "badge-yellow" : "")}>
                      {t(`leads_status_${l.status}`)}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      {l.status === "new" ? (
                        <button
                          type="button"
                          className="btn-sm btn-ghost"
                          title={t("leads_mark_handled")}
                          onClick={() => void setStatus(l, "handled")}
                        >
                          <Check size={14} />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-sm btn-ghost"
                          title={t("leads_mark_new")}
                          onClick={() => void setStatus(l, "new")}
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-sm btn-ghost"
                        title={t("leads_delete")}
                        onClick={() => setToDelete(l)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={toDelete !== null}
        title={t("leads_delete")}
        body={toDelete?.name}
        confirmLabel={t("leads_delete")}
        danger
        onConfirm={doDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
}
